export type StepKind = 'event' | 'task' | 'gateway';

export interface ProcessPiece {
  id: number;
  kind: StepKind;
  x: number;
  y: number;
  from: number[];
  wave: number;
}

type PlannedPiece = Omit<ProcessPiece, 'wave'>;

export interface EndlessProcessState {
  pieces: ProcessPiece[];
  /** Waves already planned for an open split; they are revealed before anything new is planned. */
  queuedWaves: PlannedPiece[][];
  leavingIds: number[];
  lastId: number;
  lastWave: number;
  ended: boolean;
}

export type RandomSource = () => number;

export const VIEW_WIDTH = 420;
export const VIEW_HEIGHT = 120;
export const MID_Y = VIEW_HEIGHT / 2;
export const LIVE_WAVE_LIMIT = 4;

const SINGLE_TASK_CHANCE = 0.62;
const TWO_BRANCH_CHANCE = 0.75;
const TWO_BRANCH_OFFSETS = [-28, 28];
const THREE_BRANCH_OFFSETS = [-40, 0, 40];
const CONNECTOR_LENGTH = 26;
// Branch connectors bend twice, so they need room for both corners.
const BRANCH_CONNECTOR_LENGTH = 36;
const START_X = 40;
const NEWEST_STEP_VIEW_POSITION = 0.75;

const HALF_WIDTH: Record<StepKind, number> = {
  event: 9,
  task: 24,
  // The gateway is an 18×18 square turned 45°, so it reaches half its diagonal.
  gateway: 9 * Math.SQRT2
};

export function halfWidth(kind: StepKind): number {
  return HALF_WIDTH[kind];
}

function xAfter(source: PlannedPiece, kind: StepKind, connectorLength: number): number {
  return source.x + halfWidth(source.kind) + connectorLength + halfWidth(kind);
}

/** Start event → task → task, centred in the view: the loader without motion. */
export function createStillProcess(): ProcessPiece[] {
  const start: ProcessPiece = { id: 0, kind: 'event', x: 0, y: MID_Y, from: [], wave: 0 };
  const first: ProcessPiece = {
    id: 1,
    kind: 'task',
    x: xAfter(start, 'task', CONNECTOR_LENGTH),
    y: MID_Y,
    from: [0],
    wave: 1
  };
  const second: ProcessPiece = {
    id: 2,
    kind: 'task',
    x: xAfter(first, 'task', CONNECTOR_LENGTH),
    y: MID_Y,
    from: [1],
    wave: 2
  };
  const width = second.x + halfWidth('task') - (start.x - halfWidth('event'));
  const shift = (VIEW_WIDTH - width) / 2 + halfWidth('event');
  return [start, first, second].map((piece) => ({ ...piece, x: piece.x + shift }));
}

export function createEndlessProcess(): EndlessProcessState {
  return {
    pieces: [{ id: 0, kind: 'event', x: START_X, y: MID_Y, from: [], wave: 0 }],
    queuedWaves: [],
    leavingIds: [],
    lastId: 0,
    lastWave: 0,
    ended: false
  };
}

function planWaves(
  frontier: PlannedPiece,
  firstId: number,
  random: RandomSource
): PlannedPiece[][] {
  if (random() < SINGLE_TASK_CHANCE) {
    const task: PlannedPiece = {
      id: firstId,
      kind: 'task',
      x: xAfter(frontier, 'task', CONNECTOR_LENGTH),
      y: MID_Y,
      from: [frontier.id]
    };
    return [[task]];
  }
  const split: PlannedPiece = {
    id: firstId,
    kind: 'gateway',
    x: xAfter(frontier, 'gateway', CONNECTOR_LENGTH),
    y: MID_Y,
    from: [frontier.id]
  };
  const offsets = random() < TWO_BRANCH_CHANCE ? TWO_BRANCH_OFFSETS : THREE_BRANCH_OFFSETS;
  const branchX = xAfter(split, 'task', BRANCH_CONNECTOR_LENGTH);
  const branches = offsets.map((offset, index): PlannedPiece => ({
    id: firstId + 1 + index,
    kind: 'task',
    x: branchX,
    y: MID_Y + offset,
    from: [split.id]
  }));
  const merge: PlannedPiece = {
    id: firstId + 1 + branches.length,
    kind: 'gateway',
    x: xAfter(branches[0], 'gateway', BRANCH_CONNECTOR_LENGTH),
    y: MID_Y,
    from: branches.map((branch) => branch.id)
  };
  return [[split], branches, [merge]];
}

function endEventAfter(frontier: ProcessPiece, id: number): PlannedPiece {
  return {
    id,
    kind: 'event',
    x: xAfter(frontier, 'event', CONNECTOR_LENGTH),
    y: MID_Y,
    from: [frontier.id]
  };
}

export function frontierOf(state: EndlessProcessState): ProcessPiece {
  return state.pieces[state.pieces.length - 1];
}

/** Ids of the oldest live wave once more than `LIVE_WAVE_LIMIT` waves are live. */
function oldestWaveOverLimit(pieces: ProcessPiece[], leavingIds: number[]): number[] {
  const live = pieces.filter((piece) => !leavingIds.includes(piece.id));
  const liveWaves = new Set(live.map((piece) => piece.wave));
  if (liveWaves.size <= LIVE_WAVE_LIMIT) return [];
  const oldestWave = Math.min(...liveWaves);
  return live.filter((piece) => piece.wave === oldestWave).map((piece) => piece.id);
}

/**
 * Reveals the next wave: a queued wave of an open split, a freshly planned one, or — once
 * loading is done and no split is open — the end event. The oldest wave starts leaving when
 * the live window overflows.
 */
export function revealNextWave(
  state: EndlessProcessState,
  random: RandomSource,
  loadingDone: boolean
): { state: EndlessProcessState; leavingIds: number[] } {
  const frontier = frontierOf(state);
  let queuedWaves = state.queuedWaves;
  let lastId = state.lastId;
  if (queuedWaves.length === 0) {
    queuedWaves = loadingDone
      ? [[endEventAfter(frontier, lastId + 1)]]
      : planWaves(frontier, lastId + 1, random);
    lastId = Math.max(lastId, ...queuedWaves.flat().map((piece) => piece.id));
  }
  const [planned, ...rest] = queuedWaves;
  const wave = state.lastWave + 1;
  const pieces = [...state.pieces, ...planned.map((piece) => ({ ...piece, wave }))];
  const leavingIds = oldestWaveOverLimit(pieces, state.leavingIds);
  return {
    state: {
      pieces,
      queuedWaves: rest,
      leavingIds: [...state.leavingIds, ...leavingIds],
      lastId,
      lastWave: wave,
      ended: planned[0].kind === 'event'
    },
    leavingIds
  };
}

export function removePieces(state: EndlessProcessState, ids: number[]): EndlessProcessState {
  return {
    ...state,
    pieces: state.pieces.filter((piece) => !ids.includes(piece.id)),
    leavingIds: state.leavingIds.filter((id) => !ids.includes(id))
  };
}

export function isFinishedTask(piece: ProcessPiece, state: EndlessProcessState): boolean {
  return piece.kind === 'task' && piece.wave < state.lastWave;
}

/** Horizontal camera offset that keeps the newest step at three quarters of the view. */
export function cameraOffset(state: EndlessProcessState): number {
  return Math.max(0, frontierOf(state).x - VIEW_WIDTH * NEWEST_STEP_VIEW_POSITION);
}

/** SVG path from the right side of `source` to the left side of `target`, bending between lanes. */
export function connectorPath(source: ProcessPiece, target: ProcessPiece): string {
  const startX = source.x + halfWidth(source.kind);
  const endX = target.x - halfWidth(target.kind);
  if (source.y === target.y) return `M${startX} ${source.y} H${endX}`;
  const midX = (startX + endX) / 2;
  const radius = 8;
  const direction = target.y > source.y ? 1 : -1;
  return [
    `M${startX} ${source.y}`,
    `H${midX - radius}`,
    `Q${midX} ${source.y} ${midX} ${source.y + direction * radius}`,
    `V${target.y - direction * radius}`,
    `Q${midX} ${target.y} ${midX + radius} ${target.y}`,
    `H${endX}`
  ].join(' ');
}
